import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import FilteredSelect from "../../common/components/LocationSelectionStep/FilteredSelect";
import { RootState } from "../../../../redux/store";
import { Button } from "../../../../components/ui/button";
import { Spinner } from "../../../../components/ui/spinner";
import { OpeningHours } from "../../../../components/ui/opening-hours";
import { shouldShowOpeningHours } from "../../common/locationSelection/visibility";
import { cn } from "../../../../lib/utils";
import { setNextButtonClick } from "../../../../redux/slice/temporaryStorage/temporaryStorageSlice";
import { useTemporaryStorageStepCounter } from "../hooks/useTemporaryStorageStepCounter";
import { useGetLocationListQuery } from "../../../../api/locations/locationsApiSlice";

/**
 * TemporaryStorageLocationSelectionStep
 * ---------------------------------------------------------------
 * Step component for selecting a location during temporary storage flow.
 * 
 * @remarks
 * - Reads locations from Redux store
 * - Displays location selector with opening hours preview
 * - Handles loading and empty states with proper accessibility
 * - Fixed bottom action button
 */
function TemporaryStorageLocationSelectionStep() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { current, total } = useTemporaryStorageStepCounter("locationSelection");
  const locations = useSelector(
    (state: RootState) => state.booking.useCaseLocations
  );
  const selectedLocation = useSelector(
    (state: RootState) => state.bookingCreation.selectedLocation
  );
  const { hasSelectedLocation } = useSelector(
    (state: RootState) => state.bookingCreation
  );
  const tenantConfig = useSelector((state: RootState) => state.tenantConfig.config);

  const [pulsing, setPulsing] = React.useState(false);
  
  // Check if opening hours should be displayed
  const showOpeningHoursBox = useMemo(() => {
    if (!selectedLocation) return false;
    const configAllows = tenantConfig?.showOpeningHoursInBookingFlow ?? true;
    return configAllows && shouldShowOpeningHours(selectedLocation);
  }, [selectedLocation, tenantConfig]);

  /**
   * Loading and failure are read from the query, not from the Redux mirror.
   *
   * `useCaseLocations` is initialised to `[]` and typed non-optional, so the
   * old `locations === undefined` test could never be true: the spinner branch
   * was dead code and "Kein Standort verfügbar" rendered while the request was
   * still in flight AND on every 4xx/5xx. A failure that looks like an absence
   * is the bug class this repo keeps paying for — nobody reports an empty list.
   *
   * The hook shares its cache entry with `TempStorageBookLocationDialog`, so
   * this is not a second request.
   */
  const { isLoading, isError } = useGetLocationListQuery("storage");

  const { hasLocations, validLocations } = useMemo(() => {
    const valid = Array.isArray(locations) ? locations : [];
    return {
      hasLocations: valid.length > 0,
      validLocations: valid,
    };
  }, [locations]);

  // Bounce effect when location is selected
  React.useEffect(() => {
    if (selectedLocation?.id == null) return;
    if (!hasSelectedLocation) return;
    setPulsing(true);
    const tmr = setTimeout(() => setPulsing(false), 250);
    return () => clearTimeout(tmr);
  }, [selectedLocation?.id, hasSelectedLocation]);

  const handleClickNext = () => {
    dispatch(setNextButtonClick());
  };

  /**
   * Four distinct answers, never one: still loading, failed to load, loaded but
   * genuinely empty, loaded with locations. Collapsing the first two into the
   * empty state is what made a broken request read as "there are no locations"
   * — a failure nobody reports because it looks like an absence.
   */
  const renderLocationBody = () => {
    if (isLoading) {
      return (
        <div className="flex w-full items-center justify-center py-6" aria-live="polite">
          <Spinner size="md" />
        </div>
      );
    }
    if (isError) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 py-6" role="alert">
          <p className="text-center text-slate-900 font-semibold">
            {t("book_location.error_loading_locations")}
          </p>
          <p className="text-center text-sm text-slate-600">
            {t("common.please_try_again")}
          </p>
        </div>
      );
    }
    if (!hasLocations) {
      return (
        <p className="text-slate-700" aria-live="polite">
          {t("book_location.no_location")}
        </p>
      );
    }
    return (
      <>
        <FilteredSelect locations={validLocations} />
        {showOpeningHoursBox && (
          <div className="mt-6 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="mx-auto max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <OpeningHours location={selectedLocation} showIcon useBookingFlowConfig />
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className="flex flex-col justify-center mx-5"
      role="region"
      aria-label={t("book_location.select_location")}
      aria-busy={isLoading}
    >
      {/* Step Counter */}
      <p className="-mt-5 mb-6 text-xs text-muted-foreground text-center" role="status" aria-live="polite">
        {t("temporary_storage.new_booking.step_counter", {
          current,
          total,
          defaultValue: "Schritt {{current}} von {{total}}",
        })}
      </p>

      {renderLocationBody()}

      <div className="fixed flex w-full max-w-md px-5 left-0 items-center justify-center right-0 bottom-4 m-auto">
        <Button
          size="lg"
          variant="primary"
          className={cn(
            "w-full transition-all duration-300 ease-out will-change-transform",
            pulsing && "scale-[1.03]"
          )}
          onClick={handleClickNext}
          disabled={!hasSelectedLocation}
        >
          {t("temporary_storage.new_booking.location.next_button_title")}
        </Button>
      </div>
    </div>
  );
}

export default memo(TemporaryStorageLocationSelectionStep);
